/**
 * PROJECT_RAG_TABLE: GLOBAL scope lists every non-cancelled portfolio
 * project (fetchPortfolioDashboard, matching PortfolioDashboardPage's "All
 * projects" ledger). ACCOUNT scope narrows to that account's own projects
 * (useAccountDashboard) — a capability AccountDashboardPage never exposed
 * (it doesn't render this table at all today), offered here for
 * forward-compatibility since the seeded defaults never place this builtin
 * in ACCOUNT context.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPortfolioDashboard, useAccountDashboard } from '@/api/dashboard';
import { WidgetBoundary } from '../WidgetBoundary';
import { ProjectRagTable } from '@/components/ProjectRagTable';
import { CardHeader, IconLedger } from '../CardHeader';
import { WidgetSkeleton } from '../WidgetSkeleton';
import type { BuiltinHostProps } from './hostProps';

export function ProjectRagTableHost({ title, minHeight, accountId, onRowCount }: BuiltinHostProps) {
  const portfolio = useQuery({
    queryKey: ['portfolio-dashboard'],
    queryFn: fetchPortfolioDashboard,
    enabled: !accountId,
    staleTime: 30_000,
  });
  const account = useAccountDashboard(accountId ?? '');

  const isLoading = accountId ? account.isLoading : portfolio.isLoading;
  const isError = accountId ? account.isError : portfolio.isError;
  const projects = accountId ? account.data?.projects ?? [] : portfolio.data?.projects ?? [];

  // Resolved row count for DashboardGrid's content-driven auto-height — null
  // while loading/erroring (mirrors the JSX gates below) so the pre-data MIN
  // reservation holds until a real fetch resolves, rather than snapping to a
  // premature 0. A plain number dep (not the query object/array) so the
  // effect fires only when the count itself actually changes.
  const rowCount = !isLoading && !isError ? projects.length : null;
  useEffect(() => {
    if (rowCount !== null) onRowCount?.(rowCount);
  }, [rowCount, onRowCount]);

  return (
    <WidgetBoundary label={title} minHeight={minHeight}>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader icon={<IconLedger />} title={title} style={{ marginBottom: 12, flexShrink: 0 }} />
        <div style={{ flex: 1, minHeight: 0 }}>
          {isLoading && <WidgetSkeleton />}
          {!isLoading && isError && <WidgetSkeleton variant="error" label={title} />}
          {!isLoading && !isError && <ProjectRagTable data={projects} />}
        </div>
      </div>
    </WidgetBoundary>
  );
}
